import dayjs from 'dayjs';
import { Tag } from 'antd';
import { InvoiceStatus } from '../../../../../utils/constants/tagColors';

/**
 * Helper function to create a field object
 * @function
 * @param {string} label - The label key for translation
 * @param {string|ReactNode} value - The value to display
 * @returns {Object} Field object
 */
const createField = (label, value) => ({
  label,
  span: 1,
  content: value || '-'
});

/**
 * Formats a date using dayjs
 * @function
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date
 */
const formatDate = (date) => (date && dayjs(date).format('DD/MM/YYYY')) || '-';

/**
 * Formats an amount with unit and optional negative sign for credit notes
 * @function
 * @param {number} amount - The amount
 * @param {string} unit - The currency unit
 * @param {boolean} isCreditNote - Whether it's a credit note
 * @param {string} defaultValue - Default value if amount is falsy
 * @returns {string} Formatted amount
 */
const formatAmount = (amount, unit, isCreditNote, defaultValue = '-') => {
  if (!amount) return defaultValue;
  const sign = isCreditNote ? '- ' : '';
  return `${sign}${amount} ${unit}`;
};

/**
 * Gets customer-specific fields based on invoice type
 * @function
 * @param {Object} data - Invoice data
 * @returns {Array} Array of field objects
 */
const getCustomerTypeFields = (data) => {
  const { type, associated_invoice, supervision } = data;
  const isCreditNote = type === 'CREDIT_NOTE';

  if (isCreditNote) {
    return [
      createField(
        'customerinvoices.form.associated_invoice',
        associated_invoice?.reference
      )
    ];
  }
  return [
    createField('customerinvoices.form.supervision', supervision?.reference)
  ];
};

/**
 * Gets invoicing address field based on invoice type
 * @function
 * @param {Object} data - Invoice data
 * @returns {Array} Array with invoicing address field
 */
const getInvoicingAddressField = (data) => {
  const { type, invoicing_address, city, postal_code, associated_invoice } =
    data;
  const isCreditNote = type === 'CREDIT_NOTE';

  if (isCreditNote) {
    const address =
      associated_invoice &&
      `${associated_invoice.invoicing_address}, ${associated_invoice.city}, ${associated_invoice.postal_code}`;
    return [createField('customerinvoices.form.invoicing_address', address)];
  }

  const address =
    invoicing_address && `${invoicing_address}, ${city}, ${postal_code}`;
  return [createField('customerinvoices.form.invoicing_address', address)];
};

/**
 * Gets conditional fields for customer invoices
 * @function
 * @param {Object} data - Invoice data
 * @param {string} unit - Currency unit
 * @returns {Array} Array of conditional field objects
 */
const getCustomerConditionalFields = (data, unit) => {
  const { type, due_date, total_reduction_ttc } = data;
  const isCreditNote = type === 'CREDIT_NOTE';
  const fields = [];

  if (!isCreditNote) {
    fields.push(
      createField('customerinvoices.form.due_date', formatDate(due_date)),
      createField(
        'customerinvoices.form.total_reduction_ttc',
        total_reduction_ttc ? `${total_reduction_ttc} ${unit}` : '-'
      )
    );
  }

  return fields;
};

/**
 * Fonction pour générer les informations principales d'une facture.
 * @function
 * @param {Object} data - Les données de la facture.
 * @param {Function} t - Fonction de traduction.
 * @returns {Object} Les informations principales de la facture.
 */
const principalInfo = (data, t) => {
  const {
    type,
    customer,
    payment_method,
    cheque_number,
    invoicing_address,
    city,
    postal_code,
    emission_date,
    due_date,
    total_ht,
    total_ttc,
    payment_amount,
    emitter,
    recipient_name,
    wording,
    payment_date,
    franc
  } = data;
  const unit = franc ? 'Fr' : '€';
  const isCreditNote = type === 'CREDIT_NOTE';

  const customerInfo = [
    createField(
      'customerinvoices.form.type',
      type && (
        <Tag color={InvoiceStatus[type]}>
          {t(`customerinvoices.tags.${type}`)}
        </Tag>
      )
    ),
    createField(
      'customerinvoices.form.customer',
      [
        customer?.civility ? t(`pensioners.tags.${customer.civility}`) : '',
        customer?.first_name,
        customer?.last_name
      ]
        .filter(Boolean)
        .join(' ') || '-'
    ),
    ...getCustomerTypeFields(data),
    createField(
      'customerinvoices.form.payment_method',
      payment_method &&
        t(`customerinvoices.tags.${payment_method}`, {
          number: cheque_number || ''
        })
    ),
    ...getInvoicingAddressField(data),
    createField(
      'customerinvoices.form.emission_date',
      formatDate(emission_date)
    ),
    ...getCustomerConditionalFields(data, unit),
    createField(
      'customerinvoices.form.total_ht',
      formatAmount(total_ht, unit, isCreditNote)
    ),
    createField(
      'customerinvoices.form.total_ttc',
      formatAmount(total_ttc, unit, isCreditNote, '0 €')
    ),
    createField(
      'customerinvoices.form.payment_amount',
      formatAmount(payment_amount, unit, isCreditNote)
    ),
    createField('customerinvoices.form.payment_date', formatDate(payment_date))
  ];

  const internAddress =
    invoicing_address && `${invoicing_address}, ${city}, ${postal_code}`;

  const internInfo = [
    createField('interninvoices.form.emitter', emitter),
    createField(
      'interninvoices.form.payment_method',
      payment_method ? t(`customerinvoices.tags.${payment_method}`) : '-'
    ),
    createField('interninvoices.form.recipient_name', recipient_name),
    createField('interninvoices.form.invoicing_address', internAddress),
    createField('interninvoices.form.emission_date', formatDate(emission_date)),
    createField('interninvoices.form.wording', wording),
    createField('interninvoices.form.due_date', formatDate(due_date)),
    createField(
      'interninvoices.form.total_ht',
      total_ht ? `${total_ht} ${unit}` : '-'
    ),
    createField(
      'interninvoices.form.total_ttc',
      total_ttc ? `${total_ttc} ${unit}` : '-'
    ),
    createField('customerinvoices.form.payment_date', formatDate(payment_date)),
    createField(
      'customerinvoices.form.payment_amount',
      payment_amount ? `${payment_amount} ${unit}` : '-'
    )
  ];

  return { customerInfo, internInfo };
};

export { principalInfo };
